import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import Cookies from "js-cookie";
import { makeStyles } from "@mui/styles";
import {
  GridColumns,
  DataGrid,
  GridRenderCellParams,
  GridRowParams,
  GridSelectionModel,
  GridRowHeightParams,
} from "@mui/x-data-grid";
import {
  Box,
  Chip,
  Typography,
  Grid,
  Stack,
  Modal,
  IconButton,
  Tooltip,
  Zoom,
  useMediaQuery,
  Theme,
} from "@mui/material";
import { ArrowDropDownTwoTone, HighlightOffTwoTone } from "@mui/icons-material";
import EditNoteIcon from "@mui/icons-material/EditNote";
import { SelectChangeEvent } from "@mui/material/Select";

import moment from "moment";
import { debounce } from "lodash";
import useAxios from "axios-hooks";
import { PDFViewer } from "@react-pdf/renderer";

import useFetchOrder from "hooks/useFetchOrder";

import Switch from "components/switch";
import Loader from "components/loader";
import FiltersDropDown from "components/dropdowns/filtersDropDown";
import { TableListingSkeleton } from "components/skeleton/tableListingSkeleton";
import { TableNoRowsOverlay } from "components/skeleton/tableNoRowsOverlay";
import MainCard from "components/mainCard";
import TextField from "components/textField";
import Button from "components/button";
import SearchableDropDown from "components/dropdowns/searchableDropDown";
import ScrollableModal from "components/modals/scrollableModal";
import CustomColumnExport from "components/customColumnExport";
import UtilsDropdown from "components/dropdowns/utilsDropdown";

import OrderDetail from "./orderDetail";
import PackingSlip from "./packingSlip";
import BranchFilter from "features/components/branchFilter";
import CityFilter from "features/components/cityFilter";
import { OptionSetProvider } from "features/components/optionSetContext";

import {
  headerButtonMobile,
  hideOnSmMdLg,
  hideOnXS,
  searchFieldStyle,
} from "features/businessListing/index.styles";
import { setsTableStyle } from "features/products/index.styles";
import { bulkActionsStyle } from "components/dropdowns/index.styles";
import {
  getLocalStorage,
  getCookie,
} from "features/components/helperFunctions";
import {
  bulkOrderConversionStyle,
  currencyStyles,
  filtersStyles,
  gridIconsCss,
  headerButtonSection,
  filtersParentGrid,
  headerSectionGrid,
  iconStyles,
  ordersHeadingBox,
  switchMarginUnset,
} from "./index.styles";

import { ellipseHiddenText } from "utils/styles";
import { FOPTypes, OrdersType, PusherEvent } from "./index.types";
import {
  OrderListingResponse,
  DropDownList,
  SelectedOrderDetailTypes,
  OrderListingResponseResult,
} from "utils/types";
import { ordersTypeList, bulkPrint, preAuthOrderStatus } from "utils/constants";
import { API_END_POINTS } from "utils/constants/APIEndPoints";
import {
  getUserAddress,
  getBranch,
  getPaymentMethod,
  getSourceIcon,
  verifyPreAuthStatus,
} from "utils/helperFunctions";

import { useDispatch, useSelector } from "store";
import {
  setDate,
  setGlobalSettings,
  toggleDatePicker,
} from "store/slices/Main";
import { openSnackbar } from "store/slices/snackbar";

const useStyles = makeStyles(() => ({
  colStyle1: {
    fontFamily: "Roboto",
    fontStyle: "normal",
    fontWeight: "500",
    fontSize: "14px",
    lineHeight: "16px",
    color: "#212121",
  },
  colStyle2: {
    fontWeight: "400",
    color: "#212121",
  },
}));

// exporting function to pusher component to call orders API
export let handlePusherOrderEvents: (
  eventName: string,
  orderId: number
) => void;

// variable to set updated values for get-orders API payload instead of waiting for state update
let ordersPayload = {
  searchKey: "",
  page: 0,
  exportOrders: false,
  pusherEvent: "",
};

const Orders = () => {
  const [orders, setOrders] = useState<OrderListingResponse["result"] | []>([]);
  const [packingSlipData, setPackingSlipData] = useState<
    OrderListingResponse["result"]
  >([]);

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(100);

  const [orderStatusList, setOrderStatusList] = useState<DropDownList[]>([
    { label: "All Statuses", value: "" },
  ]);
  const [selectedStatusForBulkConversion, setSelectedStatusForBulkConversion] =
    useState<DropDownList>({
      value: "",
      label: "",
    });
  const [convertToOrderStatusList, setConvertToOrderStatusList] = useState<
    DropDownList[]
  >([]);
  const [bulkOrder, setBulkOrder] = useState(false);
  const [selectedOrderStatusFilter, setSelectedOrderStatusFilter] = useState<
    string[]
  >([orderStatusList[0].label]);
  const [bulkActionsModal, setBulkActionsModal] = useState<boolean>(false);

  const [selectedOrder, setSelectedOrder] = useState<SelectedOrderDetailTypes>({
    order_id: 0,
    user_email: "",
    branch_id: "",
  });
  const [actionsValue, setActionsValue] = useState<string>("bulk_print");
  const [orderDetailModal, setOrderDetailModal] = useState<boolean>(false);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [orderTypeFilterName, setOrderTypeFilterName] = useState<string[]>([
    ordersTypeList[0].label,
  ]);
  const [selectedOrderTypeFilter, setSelectedOrderTypeFilter] = useState<
    string[]
  >([""]);
  const [canadaPost, setCanadaPost] = useState<boolean>(false);
  const [ordersExport, setOrdersExport] = useState<string>("import_export");
  const [failedPayment, setFailedPayment] = useState<boolean>(false);

  const totalOrders = useRef<number>(0);

  // storing pusher events here to utilize them in debounced function
  const capturedOrderEvents = useRef<PusherEvent[]>([]);
  const pusherOrdersFlag = useRef<boolean>(false);
  const orderGetAPIFlag = useRef<boolean>(false);

  // storing orders as key-value pair to handle orders from pusher (helps to prevent duplication & sorting issue because of order get calls using promise)
  const ordersObject = useRef<OrdersType>({});

  const { businessAllBranches, branchesAccessDetails } = useSelector(
    (state) => state.businessBranches
  );
  const { startDate, endDate, decimalPlaces, globalSettings } = useSelector(
    (state) => state.global
  );
  const { selectedBranch, selectedCity } = useSelector(
    (state) => state.dropdown
  );

  const { eatout_id, user_id } = getLocalStorage();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const classes = useStyles();

  const isScreenSmall = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  //======================================= API Call Payloads =======================================//

  // get order statuses API payload
  const getOrderStatusAPIPayload = () => {
    const formData = new FormData();

    formData.append("eatout_id", `${eatout_id}`);
    formData.append("admin_id", `${user_id}`);
    formData.append("source", "biz");
    return formData;
  };

  const bulkOrderStatusUpdatePayload = () => {
    const formData = new FormData();
    formData.append("order_id", selectionModel.join());
    formData.append(
      "bid",
      businessAllBranches?.length === 1 ? "" : `${selectedBranch}`
    );
    formData.append("status", selectedStatusForBulkConversion.label);

    formData.append("eatout_id", `${eatout_id}`);
    formData.append("eatout_uid", `${user_id}`);
    formData.append("source", "biz");
    return formData;
  };

  // get orders API payload
  const getOrdersAPIPayload = (orderId?: number, orderStatusFlag = false) => {
    const formData = new FormData();

    /**
     * order-id in case of fetching single order
     * prevent pagination for single order to get the new/updated order irrespective of page
     */
    if (orderId) {
      formData.append("order_id", `${orderId}`);
    } else {
      formData.append(
        "offset",
        ordersPayload.exportOrders ? "" : `${ordersPayload.page * pageSize}`
      );
      formData.append("limit", ordersPayload.exportOrders ? "" : `${pageSize}`);
    }

    // 'Pre-Order' => to get future orders
    formData.append(
      "type",
      pathname === "/future-orders" ? "Pre-Order" : "Live"
    );

    if (pathname !== "/future-orders") {
      formData.append("from_date", `${startDate}`);
      formData.append("to_date", `${endDate}`);
    }

    formData.append("check", "1");
    formData.append("return_statuses", "1");
    formData.append("attributes", "1");
    formData.append("key", ordersPayload.searchKey?.trim());
    // values from filters
    formData.append(
      "bid",
      businessAllBranches?.length === 1 ? "" : `${selectedBranch}`
    );
    formData.append(
      "order_type",
      canadaPost ? "0" : selectedOrderTypeFilter[0]
    );
    formData.append(
      "canada_post",
      canadaPost
        ? selectedOrderTypeFilter[0]
        : selectedOrderTypeFilter[0] === ""
        ? ""
        : "0"
    );
    formData.append("city", `${selectedCity}`);

    formData.append(
      "pre_auth",
      `${selectedOrderStatusFilter}` === "Pre-Authorized" ? "1" : "0"
    );
    formData.append(
      "status",
      !`${selectedOrderStatusFilter}` ||
        orderStatusFlag ||
        `${selectedOrderStatusFilter}` === "All Statuses"
        ? ""
        : `${selectedOrderStatusFilter}` === "Pre-Authorized"
        ? "Pending"
        : `${selectedOrderStatusFilter}` === "Cancelled"
        ? "Cancel"
        : `${selectedOrderStatusFilter}`
    );
    formData.append("payment_type", failedPayment ? "1" : "0");
    formData.append("eatout_id", `${eatout_id}`);
    formData.append("admin_id", `${user_id}`);
    formData.append("source", "biz");

    return formData;
  };

  //======================================= API Calls =======================================//

  // API call to get global settings
  const [
    { loading: globalSettingsLoading, error: globalSettingsError },
    getGlobalSettingsAPICall,
  ] = useAxios(
    {
      ...API_END_POINTS.getGlobalSettings,
      params: { restaurant_id: eatout_id, admin_id: user_id, source: "biz" },
    },
    { manual: true }
  );

  // API call to get order statuses
  const [
    { loading: orderStatusLoading, error: orderStatusError },
    getOrderStatusAPICall,
  ] = useAxios(API_END_POINTS.getOrderStatus, { manual: true });

  // bulk Status conversion
  const [
    {
      data: bulkstatusUpdate,
      loading: bulkStatusLoading,
      error: bulkStatusError,
    },
    bulkStatusUpdateAPICall,
  ] = useAxios(API_END_POINTS.bulkStatusUpdate, { manual: true });

  // custom hook to get orders
  const {
    orders: getOrdersAPICall,
    data: ordersData,
    isLoading: ordersLoading,
    isIdle,
    error: ordersError,
  } = useFetchOrder();

  //======================================= useEffect Hooks =======================================//

  useEffect(() => {
    if (getCookie("startEndDates")) {
      const { start_date, end_date } = JSON.parse(getCookie("startEndDates"));

      dispatch(
        setDate({
          startDate: start_date,
          endDate: end_date,
        })
      );
    } else {
      // set to cookies
      Cookies.set(
        "startEndDates",
        JSON.stringify({
          start_date: moment().subtract(2, "days"),
          end_date: moment(),
        })
      );
      // update store
      dispatch(
        setDate({
          startDate: moment().subtract(2, "days"),
          endDate: moment(),
        })
      );
    }

    // calling get global settings API
    callGlobalSettingsAPI();

    // reset search query field
    return () => {
      ordersPayload.searchKey = "";
    };
  }, []);

  useEffect(() => {
    setSelectionModel([]);
    setPage(0);

    // setting this value to get updated value in order API payload instead of waiting for state update
    ordersPayload.page = 0;

    /**
     * hiding date-picker for future-orders
     * if 'future-orders' => remove 'Pending' & 'Cancelled' order status
     * else if => check for 'pending' status existence (to prevent status duplication on changing dates & page size)
     */
    if (pathname === "/future-orders") {
      dispatch(toggleDatePicker(false));

      // reset order status filter
      setSelectedOrderStatusFilter(["All Statuses"]);

      // removing cancel status for 'future-orders'
      setOrderStatusList(
        orderStatusList.filter(
          (status) => status.label !== "Cancelled" && status.label !== "Pending"
        )
      );
    } else if (!orderStatusList.some((status) => status.label === "Pending")) {
      dispatch(toggleDatePicker(true));

      // adding cancel & pending statuses for 'orders'
      setOrderStatusList([
        { label: "All Statuses", value: "" },
        { label: "Pending", value: "Pending" },
        ...orderStatusList.slice(1),
        { label: "Cancelled", value: "Cancelled" },
      ]);
    }

    // calling get orders API
    callGetOrdersAPI();

    Cookies.set(
      "startEndDates",
      JSON.stringify({ start_date: startDate, end_date: endDate })
    );
  }, [startDate, endDate, pathname, pageSize, failedPayment]);

  useEffect(() => {
    if (
      ordersError ||
      globalSettingsError ||
      orderStatusError ||
      bulkStatusError
    ) {
      // notify user
      dispatch(
        openSnackbar({
          open: true,
          message: ordersError
            ? ordersError.message
            : globalSettingsError
            ? globalSettingsError.message
            : orderStatusError
            ? orderStatusError.message
            : bulkStatusError
            ? bulkStatusError.message
            : "",
          alert: {
            color: "error",
          },
        })
      );
    }
  }, [ordersError, globalSettingsError, orderStatusError, bulkStatusError]);

  //======================================= Handler Functions =======================================//

  // function to call global-settings API
  const callGlobalSettingsAPI = async () => {
    const { data } = await getGlobalSettingsAPICall();

    // status 1 => getting required response from API
    if (data?.status === "1") {
      dispatch(setGlobalSettings(data.result));

      // function to call orders statuses API
      callOrdersStatusAPI();
    }
  };

  // function to call get order statuses API
  const callOrdersStatusAPI = async () => {
    const { data } = await getOrderStatusAPICall({
      data: getOrderStatusAPIPayload(),
    });

    // status 1 => getting required response from API
    if (data?.status === "1" && Array.isArray(data?.result)) {
      const orderStatuses = [{ label: "All Statuses", value: "" }];

      data?.result?.forEach((status: string) => {
        if (pathname !== "/future-orders") {
          orderStatuses.push({
            label: status === "Cancel" ? "Cancelled" : status,
            value: status === "Cancel" ? "Cancelled" : status,
          });
        } else if (status !== "Pending" && status !== "Cancel") {
          orderStatuses.push({
            label: status,
            value: status,
          });
        }
      });

      // add "Pre-Authorized" status for Iqbal & validating pre-auth from global settings
      if (verifyPreAuthStatus(globalSettings)) {
        orderStatuses.push(preAuthOrderStatus);
      }

      // set order statuses list for filter
      setOrderStatusList(orderStatuses);
    }
  };

  // function to call orders API for filters & search & page size change & on loading orders first time & orders export
  const callGetOrdersAPI = async () => {
    // flag to prevent capturing pusher events because this call will automatically get them
    pusherOrdersFlag.current = false;

    // reset pusher event to remove restriction on api loader
    if (ordersPayload.pusherEvent) {
      ordersPayload.pusherEvent = "";
    }

    const data = await getOrdersAPICall(getOrdersAPIPayload());

    if (Array.isArray(data?.result)) {
      // set orders count for pagination
      totalOrders.current = data.count ? data.count : 0;

      // function to store orders in object to handle pusher updates
      createOrdersObject(data.result);

      // update orders state
      setOrders(data.result);

      // conditional rendering of excel export component
      if (ordersPayload.exportOrders) {
        setOrdersExport("ExportOrders");

        // setting exportOrder = false => to activate pagination in API payload
        ordersPayload.exportOrders = false;
      }

      // to continue capturing pusher events
      pusherOrdersFlag.current = true;
    } else {
      // set orders count for pagination
      totalOrders.current = 0;

      // reset orders object
      ordersObject.current = {};

      // set order list
      setOrders([]);

      // reset page no if not on first page on getting no orders
      if (ordersPayload.page) {
        setPage(0);

        // setting this value to get updated value in order API payload instead of waiting for state update
        ordersPayload.page = 0;
      }
    }
  };

  // function to return orders get API call promise
  const getOrdersPromises = (orderId: number, orderStatusFlag: boolean) => {
    /**
     * passing orderStatusFlag to disable order status filter in case of pusher events
     * doing so to get the order to verify status
     * to remove that order from particular status filter list because it's status is changed in the updated order
     */
    return getOrdersAPICall(getOrdersAPIPayload(orderId, orderStatusFlag));
  };

  // debounced function to process the pusher captured events
  const processPusherEvents = useCallback(
    debounce(async () => {
      // return if already fetching orders
      if (ordersLoading) {
        return;
      }

      // return if pusher events not exists
      if (!capturedOrderEvents?.current?.length) {
        return;
      }

      // copy current events to process and clear the original events array to capture further
      const currentEvents = [...capturedOrderEvents?.current];
      capturedOrderEvents.current = [];

      const eventsMap: { [orderEvent: string]: PusherEvent } = {};

      currentEvents.forEach((event) => {
        eventsMap[event.orderId] = event;
      });

      // get promises for orders to get details
      const ordersPromises = currentEvents?.map((event) =>
        getOrdersPromises(event.orderId, event.orderStatusFlag)
      );

      try {
        // get orders by resolving all promises
        const promisesResponse = await Promise.all(ordersPromises);

        // handle the promise response
        promisesResponse?.forEach((response) => {
          // if => verifying length and update list accordingly
          if (response?.result?.length) {
            // store order id & status for later use
            const orderId = response?.result[0]?.order_id;
            const orderStatus = response?.result[0]?.status;

            if (eventsMap[orderId]?.eventName === "NewOrder") {
              // verify order status according to status filter before adding to the list
              if (verifyOrderStatus(orderStatus)) {
                ordersObject.current[orderId] = response.result[0];

                // update the orders count to maintain pagination
                totalOrders.current += 1;
              }
            } else if (eventsMap[orderId]?.eventName === "UpdateOrder") {
              // verify order status according to status filter
              if (verifyOrderStatus(orderStatus)) {
                /**
                 * update the orders count to maintain pagination
                 * if => order doesn't exist in the list
                 * case => when order status is changed and lies in the status filter
                 */
                if (!ordersObject.current[orderId]) {
                  totalOrders.current += 1;
                }

                // add or update the order in the list
                ordersObject.current[orderId] = response.result[0];
              } else if (ordersObject.current[orderId]) {
                /**
                 * if order exists in the list
                 * case => when order status is changed and doesn't lie in the status filter
                 * remove the order
                 */
                delete ordersObject.current[orderId];

                // update the orders count to maintain pagination
                totalOrders.current -= 1;
              }
            } else {
              /**
               * case of next page orders
               * append the orders in the object
               */
              response?.result?.forEach((order: OrderListingResponseResult) => {
                ordersObject.current[order.order_id] = order;
              });
            }
          }
        });

        /**
         * update orders list
         * reverse the object values to get the latest order on top
         * Note: object performs sorting by default
         */
        setOrders(Object.values(ordersObject.current)?.reverse());
      } catch (error) {
        orderGetAPIFlag.current = true;
      } finally {
        // recall function to process any new order events that might have come in during processing
        if (capturedOrderEvents.current?.length) {
          processPusherEvents();
        } else {
          // reset pusher event to remove restriction on api loader
          ordersPayload.pusherEvent = "";
        }
      }
    }, 500),
    [capturedOrderEvents.current, ordersLoading, ordersObject.current]
  );

  // function to call getOrders API from pusher component
  handlePusherOrderEvents = function (eventName, orderId) {
    /**
     * return if fetching all orders via refresh, filters etc
     * no need to capture pusher event because fetching all orders will have them
     */
    if (!pusherOrdersFlag.current) {
      return;
    }

    /**
     * verify if same order id exists with any event (New or Update)
     * if exists => no need to repeat. We will get the updated order because of debounced function call
     */
    const orderEvent = capturedOrderEvents.current.some(
      (event) => event.orderId === orderId
    );

    if (orderEvent) {
      return;
    }

    // setting pusher-event to restrict loader on api call for order update
    ordersPayload.pusherEvent = eventName;

    // push the event to the array to process later
    capturedOrderEvents.current?.push({
      eventName,
      orderId,
      orderStatusFlag: true,
    });

    // function to call get orders API
    processPusherEvents();
  };

  // function to populate orders object to manage pusher updates later
  const createOrdersObject = (ordersList: OrderListingResponseResult[]) => {
    ordersObject.current = {};

    // create key-value pair of orders having order id as key
    ordersList?.forEach((order) => {
      ordersObject.current[order.order_id] = order;
    });
  };

  // function to verify order status (order got from pusher) according to status filter to handle in list
  const verifyOrderStatus = (status = "") => {
    if (`${selectedOrderStatusFilter}` === "All Statuses") {
      return true;
    } else {
      return (
        status ===
        (`${selectedOrderStatusFilter}` === "Pre-Authorized"
          ? "Pending"
          : `${selectedOrderStatusFilter}` === "Cancelled"
          ? "Cancel"
          : `${selectedOrderStatusFilter}`)
      );
    }
  };

  const handleSearchChange = debounce((e: { target: { value: string } }) => {
    // update orders API payload variable
    ordersPayload.searchKey = e.target.value;

    // setting this value to get updated value in order API payload instead of waiting for state update
    ordersPayload.page = 0;

    // setting page to 0 to list searched orders
    setPage(0);

    // function to call orders API
    callGetOrdersAPI();
  }, 1000);

  // order type filter handler
  const handleOrderTypeFilterChange = (
    event: SelectChangeEvent<typeof orderTypeFilterName>
  ) => {
    const {
      target: { value },
    } = event;

    let selectedOrderTypeResult: string[] = [];

    if (value && Array.isArray(value) && value.length) {
      // removing falsy values
      const filteredData = value.filter((element) => element);

      if (filteredData.length > 1) {
        /**
         * if => remove 'All order types' on selection of any other order type
         * else if => remove other order types on selection of 'All order types'
         * else => other order types only
         */
        if (filteredData[0] === "All Order Types") {
          selectedOrderTypeResult = filteredData?.filter(
            (orderType) => orderType !== "All Order Types"
          );
        } else if (
          filteredData.some((orderType) => orderType === "All Order Types")
        ) {
          selectedOrderTypeResult = ["All Order Types"];
        } else {
          selectedOrderTypeResult = filteredData?.filter(
            (orderType) => orderType !== "Select Order Type"
          );
        }
      } else {
        selectedOrderTypeResult = filteredData;
      }
    } else {
      selectedOrderTypeResult = ["Select Order Type"];
    }

    // handle flag for Canada post
    if (
      selectedOrderTypeResult?.length &&
      selectedOrderTypeResult[0] === "Canada Post"
    ) {
      setCanadaPost(true);
    } else if (canadaPost) {
      setCanadaPost(false);
    }

    // setting value for filter component
    setOrderTypeFilterName(selectedOrderTypeResult);

    // setting order type ids for API payload
    const orderTypeIds = ordersTypeList?.length
      ? ordersTypeList.reduce((orderTypeIds: string[], orderType) => {
          if (
            selectedOrderTypeResult?.some(
              (branchName) => branchName === orderType.label
            )
          ) {
            orderTypeIds.push(orderType.value);
          }
          return orderTypeIds;
        }, [])
      : [];

    // setting value for store
    setSelectedOrderTypeFilter(orderTypeIds);
  };

  const bulkOrderStatusConverstionAPI = async () => {
    const response = await bulkStatusUpdateAPICall({
      data: bulkOrderStatusUpdatePayload(),
    });

    if (response.status === 200) {
      // reset states
      setSelectedStatusForBulkConversion({ value: "", label: "" });
      setSelectionModel([]);

      // close modal
      toggleBulkActionsModal();

      // send notification
      dispatch(
        openSnackbar({
          open: true,
          message: response.data.message,
          alert: { color: "success" },
        })
      );
    } else {
      dispatch(
        openSnackbar({
          open: true,
          message: response.data.message,
          alert: { color: "error" },
        })
      );
    }
  };

  const makeListForBulkOrderStatusConverstionDropdown = (status: string[]) => {
    const availableStatusesToConvertList = [];

    for (let counter = 0; counter < orderStatusList.length; counter++) {
      if (orderStatusList[counter].label == status[0]) {
        const furtherStatuses = orderStatusList.filter(
          (status, index) => index > counter
        );

        // remove pre-auth status from bulk status conversion list
        if (verifyPreAuthStatus(globalSettings)) {
          furtherStatuses.pop();
        }
        availableStatusesToConvertList.push(...furtherStatuses);
      }
    }

    // set to state for dropdown list
    setConvertToOrderStatusList(availableStatusesToConvertList);
  };

  // order status filter handler
  const handleOrderStatusFilterChange = (
    event: SelectChangeEvent<typeof selectedOrderStatusFilter>
  ) => {
    const {
      target: { value },
    } = event;

    let selectedOrderStatusResult: string[] = [];

    if (value && Array.isArray(value) && value.length) {
      // removing falsy values
      const filteredData = value.filter((element) => element);

      if (filteredData.length > 1) {
        /**
         * if => remove 'All statuses' on selection of any other status
         * else if => remove other statuses on selection of 'All statuses'
         * else => other statuses only
         */
        if (filteredData[0] === "All Statuses") {
          selectedOrderStatusResult = filteredData?.filter(
            (status) => status !== "All Statuses"
          );
        } else if (filteredData.some((status) => status === "All Statuses")) {
          selectedOrderStatusResult = ["All Statuses"];
        } else {
          selectedOrderStatusResult = filteredData?.filter(
            (status) => status !== "Select Status"
          );
        }
      } else {
        selectedOrderStatusResult = filteredData;
      }
    } else {
      selectedOrderStatusResult = ["Select Status"];
    }

    // setting value for filter component
    setSelectedOrderStatusFilter(selectedOrderStatusResult);

    // convert only one status of orders
    makeListForBulkOrderStatusConverstionDropdown(selectedOrderStatusResult);
  };

  // function to trigger orders API call on applying filter
  const applyFilter = () => {
    setSelectionModel([]);
    setPage(0);
    // setting this value to get updated value in order API payload instead of waiting for state update
    ordersPayload.page = 0;

    // function to call orders API
    callGetOrdersAPI();
    validateBulkStatusUpdate() && setBulkOrder(true);
  };

  const addCurrency = (params: GridRenderCellParams) => {
    return (
      <Stack sx={{ alignItems: "center" }}>
        <Stack direction="row" spacing={0.25} sx={{ alignItems: "flex-end" }}>
          <Typography sx={currencyStyles}>{params.row.currency}</Typography>
          <Typography variant="subtitle1" sx={{ color: "#212121" }}>
            {parseFloat(params.value).toFixed(decimalPlaces)}
          </Typography>
        </Stack>
      </Stack>
    );
  };

  // format date column with respect to pickup/delivery
  const formatDate = (params: GridRenderCellParams) => {
    let orderDate = "";

    if (pathname === "/future-orders") {
      if (params?.row?.order_type_flag === "0") {
        orderDate = params?.row?.delivery_date;
      } else {
        orderDate = params?.row?.pickup_time;
      }
    } else {
      orderDate = params?.row?.date;
    }

    return orderDate !== "0000-00-00 00:00:00" ? (
      <Stack>
        <Typography>{moment(orderDate).format("MMM Do, YY")}</Typography>

        {!orderDate?.includes("00:00:00") && (
          <Typography>{moment(orderDate).format("hh:mm a")}</Typography>
        )}
      </Stack>
    ) : (
      "-"
    );
  };

  // order status column data
  const styledStatus = (params: GridRenderCellParams) => {
    let color = {
      borderRadius: "100px",
      fontFamily: "Roboto",
      fontStyle: "normal",
      fontWeight: 400,
      fontSize: "13px",
      background: "",
      color: "",
      padding: "",
    };

    switch (params.value) {
      case "Pending": {
        color.background = "rgb(0 0 0 / 26%)";
        color.color = "#ffffff";
        color.padding = "8px 16px";
        break;
      }
      case "Confirmed": {
        color.background = "#C7FFD6";
        color.color = "#00C853";
        color.padding = "8px 9.5px";
        break;
      }
      case "Cancelled":
      case "Cancel": {
        color.background = "#FBE9E7";
        color.color = "#D84315";
        color.padding = "8px 11px";
        break;
      }
      case "Shipped": {
        color.background = "#DCEFFF";
        color.color = "#1976D2";
        color.padding = "8px 16px";
        break;
      }
      case "Viewed": {
        color.background = "#ECEFF1";
        color.color = "#455A64";
        color.padding = "8px 18.5px";
        break;
      }
      case "Prepared": {
        color.background = "#FFF3E0";
        color.color = "#F57C00";
        color.padding = "8px 14px";
        break;
      }
      case "Dispatched": {
        color.background = "#EDE7F6";
        color.color = "#5E35B1";
        color.padding = "8px 7px";
        break;
      }
      case "Delivered": {
        color.background = "#E3F2FD";
        color.color = "#2196F3";
        color.padding = "8px 13px";
        break;
      }
      default: {
        // for in-process
        color.background = "#FFF4D0";
        color.color = "#EEB304";
        color.padding = "8px 11px";
        break;
      }
    }

    return params.row.pre_auth == "1" && params.value == "Pending" ? (
      <>
        <Stack direction={"column"} spacing={1}>
          <Chip
            label={params.value}
            sx={{
              "& .MuiChip-label": {
                p: "unset",
              },
              ...color,
            }}
          />
          <Chip
            label={"Authorized"}
            sx={{
              "& .MuiChip-label": {
                p: "unset",
              },
              ...{
                background: "#e6fffb",
                color: "#08979c",
                padding: "8px 11px",
              },
            }}
          />
        </Stack>
      </>
    ) : params.value == "Dispatched" ? (
      <Stack spacing={1}>
        <Chip
          label={params.value}
          sx={{
            "& .MuiChip-label": {
              p: "unset",
            },
            width: "fit-content",
            ...color,
          }}
        />
        {/* <Typography
          variant={"subtitle2"}
          sx={{
            textWrap: "wrap",
          }}
        >
          {calculateElapsedTime(params.row.date)}
        </Typography> */}
      </Stack>
    ) : params.value == "Cancel" ? (
      <Chip
        label={"Cancelled"}
        sx={{
          "& .MuiChip-label": {
            p: "unset",
          },
          ...color,
        }}
      />
    ) : (
      <Chip
        label={params.value}
        sx={{
          "& .MuiChip-label": {
            p: "unset",
          },
          ...color,
        }}
      />
    );
  };

  const customNoRowsOverlay = () => {
    return <TableNoRowsOverlay />;
  };

  const formatCustomerName = (params: GridRenderCellParams) => {
    return (
      <>
        <Typography sx={ellipseHiddenText} className={classes.colStyle1}>
          {params.value}
        </Typography>

        {params?.row?.note && (
          <Tooltip
            title={
              <Typography sx={{ fontSize: "13px" }}>
                {params.row.note}
              </Typography>
            }
            TransitionComponent={Zoom}
          >
            <EditNoteIcon color="disabled" sx={iconStyles} />
          </Tooltip>
        )}
      </>
    );
  };

  const handlePageChange = (pageNo: number) => {
    // stop page propagation if already fetching the next page orders
    if (pageNo > page && pageNo > Math.floor(orders.length / pageSize)) {
      return;
    }

    setSelectionModel([]);
    setPage(pageNo);

    // setting this value to get updated value in order API payload instead of waiting for state update
    ordersPayload.page = pageNo;

    // reset pusher event to remove restriction on api loader
    ordersPayload.pusherEvent = "NewPage";

    /**
     * calling orders API if coming page is greater than current page
     * and if orders list has data for the current page only
     * Math.floor() truncates the decimal part
     * (orders.length / pageSize) => gives me the count of pages data in orders list
     */
    if (pageNo > page && pageNo === Math.floor(orders.length / pageSize)) {
      capturedOrderEvents.current?.push({
        eventName: "NewPage",
        orderId: 0,
        orderStatusFlag: false,
      });

      // function to call get orders API
      processPusherEvents();
    }
  };

  const handlePageSizeChange = (pageSizeNo: number) => {
    setPageSize(pageSizeNo);
  };

  const handleOrdersExport = async () => {
    // setting exportOrder = true => to reset pagination in API payload
    ordersPayload.exportOrders = true;

    // setting page to 0 => to update orders list (getting all orders on export)
    ordersPayload.page = 0;
    setPage(0);

    // calling get orders API
    callGetOrdersAPI();
  };

  // packing slip
  const packingSlipComponent = () => {
    // filter selected orders
    const selectedOrders: OrderListingResponseResult[] = [];

    selectionModel?.reverse()?.forEach((selectedId) => {
      // returns order if found else undefined
      const result = orders?.find((order) => order.order_id === selectedId);

      if (result) {
        selectedOrders?.push(result);
      }
    });

    setPackingSlipData(selectedOrders);
  };

  const columns: GridColumns = [
    {
      field: "order_id",
      headerName: "Order #",
      headerClassName: classes.colStyle1,
      cellClassName: classes.colStyle1,
      headerAlign: "right",
      align: "right",
      flex: 0.6,
      minWidth: 76,
      sortable: false,
    },
    {
      field: "status",
      headerName: "Status",
      headerClassName: classes.colStyle1,
      headerAlign: "center",
      align: "center",
      flex: 0.9,
      minWidth: 105,
      sortable: false,
      renderCell: styledStatus,
    },
    {
      field: "name",
      headerName: "Customer Name",
      headerClassName: classes.colStyle1,
      cellClassName: classes.colStyle1,
      flex: 1.21,
      minWidth: 155,
      sortable: false,
      renderCell: formatCustomerName,
    },
    {
      field: "grand_total",
      headerName: "Grand Total",
      headerClassName: classes.colStyle1,
      headerAlign: "right",
      align: "right",
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      renderCell: addCurrency,
    },
    {
      field: "payment_type",
      headerName: "Payment",
      headerClassName: classes.colStyle1,
      headerAlign: "center",
      align: "center",
      flex: 0.6,
      minWidth: 76,
      sortable: false,
      renderCell: getPaymentMethod,
    },
    {
      field: "user_area",
      headerName: "City & Area",
      headerClassName: classes.colStyle1,
      cellClassName: classes.colStyle2,
      flex: 1.3,
      minWidth: 170,
      sortable: false,
      renderCell: getUserAddress,
    },
    {
      field: "eatout_bid",
      headerName: "Branch",
      headerClassName: classes.colStyle1,
      headerAlign: "center",
      align: "center",
      flex: 0.8,
      minWidth: 76,
      sortable: false,
      renderCell: getBranch,
    },
    {
      field: "date",
      headerName: pathname === "/future-orders" ? "Delivery Date" : "Date",
      headerClassName: classes.colStyle1,
      cellClassName: classes.colStyle1,
      flex: 0.8,
      minWidth: 98,
      sortable: false,
      renderCell: formatDate,
    },
    {
      field: "source",
      headerName: "Source",
      headerClassName: classes.colStyle1,
      cellClassName: classes.colStyle2,
      headerAlign: "center",
      align: "center",
      flex: 0.5,
      minWidth: 65,
      sortable: false,
      renderCell: getSourceIcon,
    },
  ];

  const selectStatusForBulkOrderConversion = (
    event: React.ChangeEvent<{}>,
    value: any,
    name: string
  ) => {
    if (value) {
      if (selectionModel.length) {
        setSelectedStatusForBulkConversion(value);
        setBulkActionsModal(true);
      } else {
        dispatch(
          openSnackbar({
            open: true,
            message: "Please select the orders first to Convert",
            alert: { color: "error" },
          })
        );
        setSelectedStatusForBulkConversion({ value: "", label: "" });
      }
    } else {
      // on removal of selected value
      setSelectedStatusForBulkConversion({ value: "", label: "" });
    }
  };

  const validateBulkStatusUpdate = () => {
    return (
      selectedOrderStatusFilter[0] !== "All Statuses" &&
      selectedOrderStatusFilter[0] !== "Select Status" &&
      selectedOrderStatusFilter[0] !== "Pending" &&
      selectedOrderStatusFilter[0] !== "Pre-Authorized" &&
      selectedOrderStatusFilter[0] !== "Cancelled" &&
      selectedOrderStatusFilter[0] !== "Shipped"
    );
  };

  const toggleBulkActionsModal = () =>
    setBulkActionsModal((prevState) => !prevState);

  const handleActionsChange = (
    event: SelectChangeEvent<typeof actionsValue>
  ) => {
    const {
      target: { value },
    } = event;
    packingSlipComponent();
    setActionsValue(value);
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    setFailedPayment(value);
  };

  const FPO = (style: FOPTypes) => (
    <Box sx={style}>
      <Tooltip title="Failed Payment Orders" TransitionComponent={Zoom}>
        <Typography variant="subtitle1"> FPO </Typography>
      </Tooltip>
      <Switch
        checked={failedPayment}
        name={"name"}
        sx={switchMarginUnset}
        onChange={handleSwitchChange}
      />
    </Box>
  );

  return (
    <OptionSetProvider>
      <>
        {/* prevent api call loader on update order event */}
        {(ordersLoading ||
          orderStatusLoading ||
          globalSettingsLoading ||
          bulkStatusLoading) &&
          ordersPayload.pusherEvent !== "UpdateOrder" && <Loader />}

        {ordersExport !== "import_export" && (
          <CustomColumnExport
            ordersList={orders}
            setExportType={setOrdersExport}
          />
        )}

        {/* Order Detail modal */}
        {orderDetailModal && (
          <OrderDetail
            failedPayment={failedPayment}
            selectedOrder={selectedOrder}
            orderDetailModal={orderDetailModal}
            setOrderDetailModal={setOrderDetailModal}
          />
        )}

        {/* bulk order Update Modal */}
        <ScrollableModal
          open={bulkActionsModal}
          onClose={toggleBulkActionsModal}
          paperStyle={{ width: "33vw" }}
        >
          <Stack sx={{ p: "40px" }}>
            <Grid container>
              <Grid item xs={12} sx={{ mb: "10px" }}>
                <Typography variant="h3" sx={{ color: "#212121" }}>
                  Are you sure you want to change the status?
                </Typography>
              </Grid>
            </Grid>

            <Grid container>
              <Grid item xs={12} sx={{ mb: "32px" }}>
                <Typography variant="body1" sx={{ color: "#757575" }}>
                  By clicking “Yes” you agree to apply the following bulk action
                  to the selected items.
                </Typography>
              </Grid>
            </Grid>

            <Grid container>
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "end",
                }}
              >
                <Button
                  variant={"contained"}
                  sx={{
                    p: "12px 44.5px",
                    background: "#F5F5F5",
                    color: "#212121",
                    "&:hover": {
                      backgroundColor: "#F5F5F5",
                    },
                  }}
                  onClick={toggleBulkActionsModal}
                >
                  Cancel
                </Button>

                <Button
                  variant={"contained"}
                  sx={{
                    p: "12px 33.5px",
                    ml: "12px",
                  }}
                  color={"secondary"}
                  onClick={() => {
                    bulkOrderStatusConverstionAPI();
                  }}
                >
                  Yes, Convert
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </ScrollableModal>

        <MainCard
          title={
            <Grid container>
              <Grid item xs={12} md={5} sx={headerSectionGrid}>
                <Box sx={ordersHeadingBox}>
                  <Typography variant={isScreenSmall ? "h4" : "h3"}>
                    {pathname === "/future-orders" ? "Future Orders" : "Orders"}
                  </Typography>

                  {FPO(hideOnSmMdLg)}
                </Box>

                <TextField
                  type="search"
                  placeholder="Search Order"
                  onChange={handleSearchChange}
                  sx={searchFieldStyle}
                />
              </Grid>

              <Grid item xs={12} md={7} sx={headerButtonSection}>
                {FPO(hideOnXS)}

                <UtilsDropdown
                  disabled={selectionModel.length ? false : true}
                  sx={bulkActionsStyle}
                  options={[...bulkPrint]}
                  handleChange={handleActionsChange}
                  defaultValue="bulk_print"
                  isStaticDropDown={true}
                />

                <Button
                  variant={"contained"}
                  color={"secondary"}
                  sx={
                    isScreenSmall
                      ? headerButtonMobile
                      : { ml: "13px", padding: "12px 32px !important" }
                  }
                  onClick={handleOrdersExport}
                  disabled={
                    ordersData?.length < 1 ||
                    (ordersPayload.pusherEvent !== "UpdateOrder" &&
                      ordersLoading)
                  }
                >
                  Export Orders
                </Button>
              </Grid>
            </Grid>
          } // MainCard opening tag closed here
        >
          <Grid container>
            <Grid item xs={12} md={10} sx={filtersParentGrid}>
              {/* branches filter */}
              <BranchFilter applyFilter={applyFilter} />

              {/* order type filter */}
              <FiltersDropDown
                value={orderTypeFilterName}
                onChange={handleOrderTypeFilterChange}
                dropDownList={ordersTypeList}
                sx={{ ...filtersStyles, ml: { xs: "0", md: "8px" } }}
                onChangeButton={applyFilter}
                disableButton={orderTypeFilterName[0] === "Select Order Type"}
              />

              {/* city filter */}
              <Stack sx={{ ml: { xs: "0", md: "8px" } }}>
                <CityFilter applyFilter={applyFilter} />
              </Stack>

              {/* order status filter */}
              <FiltersDropDown
                value={selectedOrderStatusFilter}
                onChange={handleOrderStatusFilterChange}
                dropDownList={orderStatusList}
                sx={{ ...filtersStyles, ml: { xs: "0", md: "8px" } }}
                onChangeButton={applyFilter}
                disableButton={selectedOrderStatusFilter[0] === "Select Status"}
              />
            </Grid>

            <Grid item xs={12} md={2} sx={{ ...gridIconsCss, mb: "16px" }}>
              {bulkOrder && validateBulkStatusUpdate() && (
                <SearchableDropDown
                  placeholder="Bulk Status Update"
                  value={selectedStatusForBulkConversion}
                  options={convertToOrderStatusList}
                  popupIcon={<ArrowDropDownTwoTone />}
                  handleChange={selectStatusForBulkOrderConversion}
                  sx={bulkOrderConversionStyle}
                />
              )}
            </Grid>
          </Grid>

          {selectionModel.length > 0 && (
            <Grid container>
              <Grid item xs={12}>
                <Typography
                  variant="h5"
                  sx={{
                    mb: "20px",
                    color: "#212121",
                  }}
                >
                  {`${selectionModel.length} Order(s)`}
                </Typography>
              </Grid>
            </Grid>
          )}

          <Box sx={{ ...setsTableStyle, mb: "0" }}>
            {isIdle ? (
              <TableListingSkeleton />
            ) : (
              //  listing orders
              <DataGrid
                rows={orders}
                columns={columns}
                rowCount={totalOrders.current}
                getRowId={(r) => r.order_id}
                rowsPerPageOptions={[50, 100]}
                page={page}
                onPageChange={handlePageChange}
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
                checkboxSelection
                autoHeight
                disableColumnMenu
                hideFooterSelectedRowCount
                onRowClick={(params: GridRowParams) => {
                  // remove any checkbox selection
                  setSelectionModel([]);

                  setSelectedOrder({
                    order_id: params.row.order_id,
                    user_email: params.row.user_email,
                    branch_id: params.row.eatout_bid,
                  });
                  setOrderDetailModal(true);
                }}
                onSelectionModelChange={(newSelectionModel) => {
                  setSelectionModel(newSelectionModel);
                }}
                selectionModel={selectionModel}
                components={{
                  NoRowsOverlay: customNoRowsOverlay,
                }}
                getRowHeight={({ model }: GridRowHeightParams) => {
                  if (model.pre_auth === "1" && model.status === "Pending") {
                    return 90;
                  } else {
                    return null;
                  }
                }}
              />
            )}
          </Box>

          <Modal
            open={packingSlipData?.length > 0}
            sx={{ width: "100vw", height: "100vh" }}
          >
            <MainCard
              title={
                <Stack
                  direction="row"
                  sx={{ justifyContent: "space-between", alignItems: "center" }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: "190px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant={"h2"}>Print Preview</Typography>
                  </Box>

                  <IconButton
                    sx={{ p: "unset" }}
                    onClick={() => setPackingSlipData([])}
                  >
                    <HighlightOffTwoTone
                      sx={{ color: "#D84315" }}
                      fontSize="large"
                    />
                  </IconButton>
                </Stack>
              }
            >
              <PDFViewer style={{ width: "95vw", height: "95vh" }}>
                <PackingSlip
                  branchesAccessDetails={branchesAccessDetails}
                  packingSlipData={packingSlipData}
                  listCategories={actionsValue === "pickup_slip" && true}
                />
              </PDFViewer>
            </MainCard>
          </Modal>
        </MainCard>
      </>
    </OptionSetProvider>
  );
};

export default Orders;
